import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Button,
  List,
  Tooltip,
  Tag,
  Empty,
  Tabs,
  Skeleton,
} from 'antd';
import {
  EyeOutlined,
  FilePdfOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LeftOutlined,
  MoreOutlined,
  FileTextOutlined,
  CalendarOutlined,
  FileImageOutlined,
  ExperimentOutlined,
  HeartOutlined,
  FolderOutlined,
  MedicineBoxOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { getAccessTokenApi } from '../../api/auth';
import { getPdfApi } from '../../api/cloudStorage';
import dayjs from 'dayjs';
import PdfViewer from '../../components/PdfViewer';
import Spin from '../../components/Spin';
import { getPatientStudiesApi, getPatientPentalogicStudiesApi } from '../../api/patient';
import '../../scss/portal-list.scss';
import './PdfReports.scss';
import { useSelector } from 'react-redux';

const DEFAULT_REPORT_TYPES = ['Laboratorio', 'Cardio', 'Imagen'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

function fileExtension(url) {
  const cleanUrl = String(url || '')
    .split('?')[0]
    .split('#')[0];
  return cleanUrl.split('.').pop().toLowerCase();
}

function ImagePreview({ src, fullSize }) {
  return (
    <div className={fullSize ? 'image-preview image-preview--full' : 'image-preview'}>
      <div className="image-preview__stage">
        <img src={src} alt="Estudio" />
      </div>
    </div>
  );
}

function sortStudiesByDate(studies) {
  const list = Array.isArray(studies) ? studies : [];
  return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
}

function getTypeIcon(type) {
  switch (type) {
    case 'Laboratorio':
      return <ExperimentOutlined />;
    case 'Cardio':
      return <HeartOutlined />;
    case 'Imagen':
      return <FileImageOutlined />;
    case 'Clinica':
      return <MedicineBoxOutlined />;
    default:
      return <FolderOutlined />;
  }
}

function getTagColor(type) {
  switch (type) {
    case 'Laboratorio':
      return 'blue';
    case 'Cardio':
      return 'red';
    case 'Imagen':
      return 'green';
    case 'Clinica':
      return 'orange';
    case 'Radiografía':
      return '#d4ead8';
    case 'Tomografía':
      return '#cfe8e4';
    case 'Resonancia':
      return '#e4d8f0';
    case 'Ecografía':
      return '#f8ebc4';
    case 'Mamografía':
      return '#f6d6e4';
    case 'Medicina nuclear':
      return '#f8e0c8';
    case 'Angiografía':
      return '#f3d5ce';
    case 'Radioscopia':
      return '#d5e3f6';
    case 'Estudio de imágenes':
      return '#e4e4e4';
    default:
      return 'purple';
  }
}

function StudyItem({ pdfReport, viewPdf, openExternal, loadingUpload }) {
  const typeLabel =
    pdfReport?.type && pdfReport.type !== 'false' && pdfReport.type !== (pdfReport.viewTitle || pdfReport.title)
      ? pdfReport.type
      : null;
  const isPentalogic = pdfReport?.externalSource === 'pentalogic';
  const resources = isPentalogic
    ? [...(pdfReport.images || []), ...(pdfReport.informes || [])]
    : [];

  return (
    <List.Item
      className="portal-list-item"
      actions={[
        <Tooltip title="Ver estudio" key="view">
          <Button
            type="link"
            className="portal-list-action-btn portal-list-action-btn--view"
            onClick={() => viewPdf(pdfReport)}
            disabled={loadingUpload}
            icon={<EyeOutlined />}
          />
        </Tooltip>,
      ]}
    >
      <List.Item.Meta
        avatar={
          <div className="portal-list-avatar portal-list-avatar--study">
            {getTypeIcon(pdfReport?.type)}
          </div>
        }
        title={
          <div className="portal-list-title-row">
            <span className="portal-list-title">
              {pdfReport?.viewTitle || pdfReport?.title}
            </span>
            {typeLabel && <Tag color={getTagColor(typeLabel)}>{typeLabel}</Tag>}
          </div>
        }
        description={
          <div className="portal-list-meta">
            {pdfReport?.date && (
              <span className="portal-list-meta__item">
                <CalendarOutlined />
                {dayjs(pdfReport.date).format('DD MMM YYYY')}
              </span>
            )}
            {pdfReport?.doctor && (
              <span className="portal-list-meta__item">
                {pdfReport.doctor}
              </span>
            )}
            {resources.map((resource, index) => (
              <Button
                key={`${resource.kind}-${index}`}
                type="link"
                size="small"
                onClick={() => openExternal(resource)}
              >
                {resource.kind === 'image' && /^\d{2}:\d{2}$/.test(resource.label)
                  ? `Imagen ${resource.label}`
                  : resource.label}
              </Button>
            ))}
          </div>
        }
      />
    </List.Item>
  );
}

export default function PdfReports() {
  const [pdfReports, setPdfReports] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingPentalogic, setLoadingPentalogic] = useState(false);
  const [pentalogicFailed, setPentalogicFailed] = useState(false);
  const loadedKey = useRef('');
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [pdf, setPdf] = useState();
  const [pdfName, setPdfName] = useState();
  const [extension, setExtension] = useState();
  const [imageFullSize, setImageFullSize] = useState(false);
  const [typeFilter, setTypeFilter] = useState('Todos');
  const [sortOrder, setSortOrder] = useState('desc');

  const id = useSelector((store) => store.userData.user._id);
  const { tenant } = useSelector((store) => store.tenantData);
  const tenantConfig = useSelector(
    (store) => store.tenantData.tenantConfigurations
  );

  const reportTypes = tenantConfig?.reportTypes || DEFAULT_REPORT_TYPES;
  const configuredTypeNames = (reportTypes || [])
    .map((type) => (typeof type === 'string' ? type : type?.name))
    .filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    const key = String(id);
    if (loadedKey.current !== key) {
      loadedKey.current = key;
      setPdfReports(undefined);
    }

    const getData = async () => {
      setLoading(true);
      setLoadingPentalogic(false);
      setPentalogicFailed(false);
      const token = await getAccessTokenApi();
      if (!token || cancelled) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const response = await getPatientStudiesApi(id, token);
        if (cancelled) return;

        const local = sortStudiesByDate(response?.studies);
        setPdfReports(local);
        setLoading(false);

        if (!response?.hasPentalogic) return;

        setLoadingPentalogic(true);
        const penta = await getPatientPentalogicStudiesApi(id, token);
        if (cancelled) return;

        if (!penta || typeof penta !== 'object' || penta.failed) {
          setPentalogicFailed(true);
          return;
        }

        const externalStudies = Array.isArray(penta.studies) ? penta.studies : [];
        setPdfReports(sortStudiesByDate([...local, ...externalStudies]));
      } catch (err) {
        console.log(err);
        if (!cancelled) setPdfReports((current) => current || []);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingPentalogic(false);
        }
      }
    };

    getData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const getReportCount = (type) => {
    if (!pdfReports) return 0;
    return type === 'Todos'
      ? pdfReports.length
      : pdfReports.filter((report) => report?.type === type).length;
  };

  const tabItems = useMemo(() => {
    const items = [
      {
        key: 'Todos',
        label: (
          <span className="tab-label">
            <span>Todos</span>
            {getReportCount('Todos') > 0 && (
              <span className="count-badge">{getReportCount('Todos')}</span>
            )}
          </span>
        ),
        icon: <FileTextOutlined />,
      },
    ];

    const pentalogicTypeNames = [
      ...new Set(
        (pdfReports || [])
          .filter((study) => study?.externalSource === 'pentalogic' && study.type)
          .map((study) => study.type)
      ),
    ].filter((type) => !configuredTypeNames.includes(type));

    [...configuredTypeNames, ...pentalogicTypeNames].forEach((type) => {
      if (getReportCount(type) > 0) {
        items.push({
          key: type,
          label: (
            <span className="tab-label">
              <span>{type}</span>
              <span className="count-badge">{getReportCount(type)}</span>
            </span>
          ),
          icon: getTypeIcon(type),
        });
      }
    });

    return items;
  }, [pdfReports, reportTypes, configuredTypeNames]);

  const filteredReports = useMemo(() => {
    if (!pdfReports) return [];

    const filtered =
      typeFilter === 'Todos'
        ? pdfReports
        : pdfReports.filter((report) => report?.type === typeFilter);

    return [...filtered].sort((a, b) =>
      sortOrder === 'desc'
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    );
  }, [pdfReports, typeFilter, sortOrder]);

  const openExternal = (resource) => {
    if (!resource?.url) return;
    if (window.screen.width <= 800) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
      return;
    }
    setLoadingUpload(false);
    setImageFullSize(false);
    setPdf(resource.url);
    setPdfName(resource.label);
    setExtension('external');
  };

  const viewPdf = async (pdfReport) => {
    if (pdfReport.externalSource === 'pentalogic') {
      const resource = pdfReport.informes?.[0] || pdfReport.images?.[0];
      if (resource) openExternal(resource);
      return;
    }
    const token = await getAccessTokenApi();
    setLoadingUpload(true);
    setImageFullSize(false);
    setPdfName(pdfReport.viewTitle || pdfReport.title);
    await getPdfApi(
      token,
      pdfReport?.id ? pdfReport.id : id,
      pdfReport.title || pdfReport,
      tenant
    ).then((response) => {
      setPdf(response.url);
      setExtension(fileExtension(response.url));
      setLoadingUpload(false);
      if (window.screen.width <= 800) {
        window.open(response.url);
      }
    });
  };

  const isImage = IMAGE_EXTENSIONS.includes(extension);

  if (pdf && window.screen.width > 800) {
    return (
      <div className="studies-panel">
        <Button
          type="link"
          className="portal-viewer-back"
          icon={<LeftOutlined />}
          onClick={() => setPdf()}
        >
          Volver al listado
        </Button>
        <div className="portal-viewer-header">
          <span className="portal-list-title">{pdfName}</span>
          <div className="portal-viewer-actions">
            {extension === 'pdf' ? (
              <Tooltip title="Descargar">
                <a href={pdf} target="_blank" rel="noopener noreferrer">
                  <Button type="primary" icon={<FilePdfOutlined />} size="small">
                    Descargar
                  </Button>
                </a>
              </Tooltip>
            ) : (
              <Tooltip title="Abrir en otra pestaña">
                <a href={pdf} target="_blank" rel="noopener noreferrer">
                  <Button type="link" size="large" icon={<EyeOutlined />} />
                </a>
              </Tooltip>
            )}
            {isImage && (
              <Tooltip title={imageFullSize ? 'Ajustar a la vista' : 'Tamaño completo'}>
                <Button
                  type="link"
                  size="large"
                  icon={imageFullSize ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={() => setImageFullSize((current) => !current)}
                />
              </Tooltip>
            )}
          </div>
        </div>
        <div className="portal-viewer">
          {loadingUpload ? (
            <Spin />
          ) : extension === 'external' ? (
            <iframe
              className="portal-external-frame"
              title={pdfName || 'Estudio'}
              src={pdf}
            />
          ) : extension === 'pdf' ? (
            <PdfViewer pdf={pdf} />
          ) : isImage ? (
            <ImagePreview src={pdf} fullSize={imageFullSize} />
          ) : (
            <embed src={pdf} style={{ width: '100%', height: '100%' }} title={pdfName} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="studies-panel">
      <div className="portal-list-toolbar">
        <div className="portal-list-filters">
          <Tabs
            activeKey={typeFilter}
            onChange={setTypeFilter}
            items={tabItems}
            size="small"
            className="portal-type-tabs"
            moreIcon={<MoreOutlined />}
          />
        </div>
        <div className="portal-list-controls">
          <Tooltip
            title={
              sortOrder === 'desc'
                ? 'Más reciente primero'
                : 'Más antiguo primero'
            }
          >
            <Button
              type="text"
              icon={
                sortOrder === 'desc' ? (
                  <SortDescendingOutlined />
                ) : (
                  <SortAscendingOutlined />
                )
              }
              onClick={() =>
                setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
              }
            />
          </Tooltip>
        </div>
      </div>

      {loading && (!pdfReports || pdfReports.length === 0) ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : filteredReports.length > 0 ? (
        <List
          className="studies-list"
          itemLayout="horizontal"
          dataSource={filteredReports}
          renderItem={(pdfReport) => (
            <StudyItem
              key={`${pdfReport.externalSource || 'local'}-${pdfReport.accession_no || pdfReport.title}-${pdfReport.date}`}
              pdfReport={pdfReport}
              viewPdf={viewPdf}
              openExternal={openExternal}
              loadingUpload={loadingUpload}
            />
          )}
        />
      ) : !loadingPentalogic && !pentalogicFailed && pdfReports?.length > 0 ? (
        <Empty
          description={`No hay estudios de tipo ${typeFilter}`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : !loadingPentalogic && !pentalogicFailed ? (
        <Empty
          description="No se han encontrado estudios"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : null}
      {(loadingPentalogic || pentalogicFailed) && (
        <div
          className={`studies-external-status${
            !pdfReports || pdfReports.length === 0 ? ' studies-external-status--alone' : ''
          }`}
        >
          {loadingPentalogic
            ? 'Cargando estudios externos...'
            : 'No se pudieron cargar los estudios externos.'}
        </div>
      )}
    </div>
  );
}
